import { Controller } from "@hotwired/stimulus"
import { createLogger } from "../lib/logger"
import { getI18nMessage } from "../lib/i18n_helper"
import { safeFocus, isIOSWebView } from "../lib/ios_focus_guard"

// SearchAutocompleteController
// 실시간 검색 자동완성 UI
//
// UX 법칙:
// - 히크의 법칙: 5개 제한으로 선택 부담 감소
// - 밀러의 법칙: 트렌딩 5개로 인지 부하 최소화
// - 테슬러의 법칙: 자동완성으로 타이핑 최소화
// - 점진적 공개: 포커스→트렌딩, 입력→추천
// - 피크엔드 법칙: 빠른 응답 + 성공적 결과
//
// Targets:
// - input: 검색 입력 필드
// - dropdown: 자동완성 드롭다운
// - suggestions: 추천 검색어 컨테이너
// - recent: 최근 검색어 컨테이너
//
// Values:
// - url: 자동완성 API URL
// - debounce: 디바운스 시간 (ms)
// - minLength: 최소 입력 길이
const log = createLogger('SearchAutocomplete')

export default class extends Controller {
  static targets = ["input", "dropdown", "suggestions", "recent", "clearButton"]
  static values = {
    url: String,
    debounce: { type: Number, default: 300 },
    minLength: { type: Number, default: 2 }
  }

  connect() {
    log.debug('connect')
    this.debounceTimer = null
    this.selectedIndex = -1

    // 외부 클릭으로 드롭다운 닫기
    this.boundHideOnClickOutside = this.hideOnClickOutside.bind(this)
    document.addEventListener("click", this.boundHideOnClickOutside)

    // 스크롤 시 드롭다운 닫기
    // iOS: touchstart가 user gesture chain을 끊어 focus 실패 유발 → scroll만 사용
    // Android: touch 이벤트로 즉시 반응
    this.boundHideOnScroll = this.hideOnScroll.bind(this)
    if (!isIOSWebView()) {
      window.addEventListener("touchstart", this.boundHideOnScroll, { passive: true })
      window.addEventListener("touchmove", this.boundHideOnScroll, { passive: true })
    }
    window.addEventListener("scroll", this.boundHideOnScroll, { passive: true })
  }

  disconnect() {
    document.removeEventListener("click", this.boundHideOnClickOutside)
    if (!isIOSWebView()) {
      window.removeEventListener("touchstart", this.boundHideOnScroll)
      window.removeEventListener("touchmove", this.boundHideOnScroll)
    }
    window.removeEventListener("scroll", this.boundHideOnScroll)
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }
  }

  // Input 포커스 시 (점진적 공개)
  onFocus() {
    const query = this.inputTarget.value.trim()

    if (query.length === 0) {
      // 빈 입력 → 트렌딩 검색어 표시
      this.showRecent()
    } else if (query.length >= this.minLengthValue) {
      // 기존 입력 → 자동완성
      this.fetchSuggestions(query)
    }
  }

  // Input 값 변경 시
  onInput(event) {
    const query = event.target.value.trim()

    // Clear 버튼 표시/숨김
    this.toggleClearButton(query.length > 0)

    // 디바운스로 API 호출 최적화
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer)
    }

    if (query.length === 0) {
      this.showRecent()
      return
    }

    if (query.length < this.minLengthValue) {
      this.hideDropdown()
      return
    }

    // 디바운스: 300ms 후 API 호출
    this.debounceTimer = setTimeout(() => {
      this.fetchSuggestions(query)
    }, this.debounceValue)
  }

  // 키보드 네비게이션
  onKeydown(event) {
    const items = this.getDropdownItems()

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.selectedIndex = Math.min(this.selectedIndex + 1, items.length - 1)
        this.updateSelection(items)
        break
      case "ArrowUp":
        event.preventDefault()
        this.selectedIndex = Math.max(this.selectedIndex - 1, -1)
        this.updateSelection(items)
        break
      case "Enter":
        event.preventDefault()
        if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
          this.selectItem(items[this.selectedIndex])
        } else {
          this.submitSearch()
        }
        break
      case "Escape":
        this.hideDropdown()
        this.inputTarget.blur()
        break
    }
  }

  // Clear 버튼 클릭
  clear() {
    this.inputTarget.value = ""
    this.toggleClearButton(false)
    this.showRecent()
    safeFocus(this.inputTarget)
  }

  // 제안 선택
  async selectSuggestion(event) {
    event.preventDefault()
    const clickedQuery = event.currentTarget.dataset.query
    const originalQuery = this.inputTarget.value.trim()

    // Position 계산 (0-based index)
    const items = this.getDropdownItems()
    const position = Array.from(items).indexOf(event.currentTarget)

    // Telemetry: 제안 클릭 추적
    await this.trackSuggestionClick(originalQuery, clickedQuery, position)

    // 검색 실행
    this.inputTarget.value = clickedQuery
    this.submitSearch()
  }

  // 검색 제출
  submitSearch() {
    const query = this.inputTarget.value.trim()
    if (query.length > 0) {
      // requestSubmit → Turbo visit (native shells push it, so a tab re-tap pops back to the list)
      this.element.closest("form").requestSubmit()
    }
  }

  // API: 자동완성 데이터 가져오기
  async fetchSuggestions(query) {
    try {
      const url = `${this.urlValue}?q=${encodeURIComponent(query)}`
      const token = document.querySelector('meta[name="csrf-token"]')?.content

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          "Accept": "application/json",
          ...(token && { 'X-CSRF-Token': token })
        },
        credentials: 'same-origin'  // 세션 쿠키 포함
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch suggestions`)
      }

      const data = await response.json()
      log.debug('Autocomplete response', data)
      this.renderSuggestions(
        data.suggestions || [],
        data.recent_searches || [],
        data.trending_searches || [],
        data.popular_categories || []
      )
    } catch (error) {
      log.error('Autocomplete error', error)
      const errorMsg = getI18nMessage(
        this.element,
        'errorsLoadSuggestions',
        '無法載入搜尋建議'
      )
      this.showErrorState(errorMsg)
    }
  }

  // UI: 추천 검색어 렌더링
  // UX 개선: suggestions가 없어도 Discovery Mode(카테고리)를 표시
  renderSuggestions(suggestions, recentSearches, trendingSearches = [], popularCategories = []) {
    // 모든 섹션이 비어있을 때만 숨김
    if (suggestions.length === 0 &&
        recentSearches.length === 0 &&
        trendingSearches.length === 0 &&
        popularCategories.length === 0) {
      this.hideDropdown()
      return
    }

    // suggestions가 없지만 Discovery 데이터가 있으면 Discovery Mode 사용
    if (suggestions.length === 0 && trendingSearches.length > 0) {
      this.showDiscoveryState({
        trending_searches: trendingSearches
      })
      return
    }

    // 추천 검색어
    if (suggestions.length > 0) {
      this.suggestionsTarget.innerHTML = suggestions.map(item => `
        <button
          type="button"
          class="autocomplete-item w-full text-left px-4 py-3 hover:bg-charcoal-50 active:bg-charcoal-100 transition-colors flex items-center gap-3"
          data-action="click->search-autocomplete#selectSuggestion"
          data-query="${this.escapeHtml(item.query)}"
        >
          <svg class="w-5 h-5 text-charcoal-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <span class="text-base text-charcoal-900">${this.escapeHtml(item.query)}</span>
        </button>
      `).join("")
      this.suggestionsTarget.classList.remove("hidden")
    } else {
      this.suggestionsTarget.classList.add("hidden")
    }

    // Hide recent searches container (recent searches removed)
    this.recentTarget.classList.add("hidden")

    this.showDropdown()
  }

  // UI: Discovery 모드 표시 (트렌딩 검색어)
  async showRecent() {
    const token = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(this.urlValue, {
        method: 'GET',
        headers: {
          "Accept": "application/json",
          ...(token && { 'X-CSRF-Token': token })
        },
        credentials: 'same-origin'  // 세션 쿠키 포함
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch recent searches`)
      }

      const data = await response.json()
      log.debug('Recent searches response', data)

      // Discovery Mode: 트렌딩 + 카테고리 + 최근 검색어
      this.showDiscoveryState(data)
    } catch (error) {
      log.error('Recent searches error', error)
      const errorMsg = getI18nMessage(
        this.element,
        'errorsRecentLoad',
        '無法載入最近搜尋'
      )
      this.showErrorState(errorMsg)
    }
  }

  // UI: Discovery Mode 표시 (트렌딩 검색어만)
  // Hick's Law: 5개 선택지로 최적화
  // UX 개선: 최근 검색어 + 카테고리 제거하여 모바일 UX 간소화
  showDiscoveryState(data) {
    const { trending_searches = [] } = data

    let html = ''

    // 트렌딩 검색어만 표시 (최근 검색어 제거됨)
    if (trending_searches.length > 0) {
      html += this.renderTrendingSection(trending_searches)
    }

    // 모두 비어있으면 최소 힌트 표시
    if (!html) {
      html = this.renderMinimalHint()
    }

    this.suggestionsTarget.classList.add("hidden")
    this.recentTarget.innerHTML = html
    this.recentTarget.classList.remove("hidden")
    this.showDropdown()
  }

  // 트렌딩 검색어 섹션 렌더링
  // UX 개선: 🔥 아이콘 + crust-600 스타일 헤더 + 순위 번호
  renderTrendingSection(items) {
    const label = getI18nMessage(this.element, 'labelPopular', '現時熱門搜尋')
    return `
      <div class="px-4 py-2 text-xs font-medium text-crust-600 bg-charcoal-50 flex items-center gap-1">
        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" class="flex-shrink-0">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
          <path d="M10 2c0 -.88 1.056 -1.331 1.692 -.722c1.958 1.876 3.096 5.995 1.75 9.12l-.08 .174l.012 .003c.625 .133 1.203 -.43 2.303 -2.173l.14 -.224a1 1 0 0 1 1.582 -.153c1.334 1.435 2.601 4.377 2.601 6.27c0 4.265 -3.591 7.705 -8 7.705s-8 -3.44 -8 -7.706c0 -2.252 1.022 -4.716 2.632 -6.301l.605 -.589c.241 -.236 .434 -.43 .618 -.624c1.43 -1.512 2.145 -2.924 2.145 -4.78" />
        </svg>
        ${this.escapeHtml(label)}
      </div>
      ${items.slice(0, 5).map((item, index) => this.renderTrendingItem(item.query, index)).join('')}
    `
  }

  // 트렌딩 검색 아이템 렌더링 (순위 번호 + 간소화된 스타일)
  // @param query [String] 검색어
  // @param index [Integer] 0-based 인덱스 (01.~05. 순위 표시용)
  renderTrendingItem(query, index) {
    const ranking = String(index + 1).padStart(2, '0') + '.'

    return `
      <button
        type="button"
        class="autocomplete-item w-full text-left px-4 py-3 hover:bg-charcoal-50 active:bg-charcoal-100 transition-colors flex items-center gap-3 min-h-[44px]"
        data-action="click->search-autocomplete#selectSuggestion"
        data-query="${this.escapeHtml(query)}"
      >
        <span class="text-lg font-bold text-crust-600 flex-shrink-0 w-8">${ranking}</span>
        <span class="text-base text-charcoal-900">${this.escapeHtml(query)}</span>
      </button>
    `
  }

  // 최소 힌트 표시 (모든 데이터 없을 때)
  renderMinimalHint() {
    const hintMsg = getI18nMessage(this.element, 'emptyHint', '請輸入搜尋關鍵字')
    return `
      <div class="px-4 py-6 text-center">
        <p class="text-sm text-charcoal-500">${this.escapeHtml(hintMsg)}</p>
      </div>
    `
  }

  // UI: 에러 상태 표시 (API 호출 실패)
  showErrorState(message) {
    const retryMsg = getI18nMessage(
      this.element,
      'errorsRetry',
      '請稍後再試'
    )

    this.suggestionsTarget.classList.add("hidden")
    this.recentTarget.innerHTML = `
      <div class="px-6 py-8 text-center">
        <svg class="mx-auto h-12 w-12 text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <p class="mt-4 text-sm text-red-500">${this.escapeHtml(message)}</p>
        <p class="mt-1 text-xs text-charcoal-400">${this.escapeHtml(retryMsg)}</p>
      </div>
    `
    this.recentTarget.classList.remove("hidden")
    this.showDropdown()
  }

  // UI: 드롭다운 표시
  showDropdown() {
    this.dropdownTarget.classList.remove("hidden")
    this.selectedIndex = -1
  }

  // UI: 드롭다운 숨김
  hideDropdown() {
    this.dropdownTarget.classList.add("hidden")
    this.selectedIndex = -1
  }

  // UI: Clear 버튼 토글
  toggleClearButton(show) {
    if (this.hasClearButtonTarget) {
      if (show) {
        this.clearButtonTarget.classList.remove("hidden")
      } else {
        this.clearButtonTarget.classList.add("hidden")
      }
    }
  }

  // 외부 클릭 감지
  hideOnClickOutside(event) {
    if (!this.element.contains(event.target)) {
      this.hideDropdown()
    }
  }

  // 스크롤 시 드롭다운 닫기
  hideOnScroll(event) {
    // 입력창 내부 터치는 무시 (드롭다운 선택 가능하게)
    if (event.type.startsWith("touch") && this.element.contains(event.target)) {
      return
    }
    if (!this.dropdownTarget.classList.contains("hidden")) {
      this.hideDropdown()
      this.inputTarget.blur()
    }
  }

  // 키보드 선택 업데이트
  getDropdownItems() {
    return Array.from(this.element.querySelectorAll(".autocomplete-item"))
  }

  updateSelection(items) {
    items.forEach((item, index) => {
      if (index === this.selectedIndex) {
        item.classList.add("bg-charcoal-100")
        item.scrollIntoView({ block: "nearest" })
      } else {
        item.classList.remove("bg-charcoal-100")
      }
    })
  }

  async selectItem(item) {
    const clickedQuery = item.dataset.query
    const originalQuery = this.inputTarget.value.trim()

    // Position 계산 (키보드 선택 시 selectedIndex 사용)
    const position = this.selectedIndex

    // Telemetry: 제안 클릭 추적
    await this.trackSuggestionClick(originalQuery, clickedQuery, position)

    // 검색 실행
    this.inputTarget.value = clickedQuery
    this.submitSearch()
  }

  // Telemetry: 제안 클릭 이벤트 전송
  // @param originalQuery [String] 원래 입력한 검색어
  // @param clickedQuery [String] 클릭한 제안 검색어
  // @param position [Integer] 제안 목록에서의 위치 (0-based)
  async trackSuggestionClick(originalQuery, clickedQuery, position) {
    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content

      const response = await fetch('/search/track_suggestion_click', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token && { 'X-CSRF-Token': token })
        },
        credentials: 'same-origin',
        body: JSON.stringify({
          query: originalQuery,
          clicked: clickedQuery,
          position: position
        })
      })

      if (!response.ok) {
        log.warn('Failed to track suggestion click', { status: response.status })
      }
    } catch (error) {
      // Tracking 실패는 사용자 경험에 영향 없음 (silent fail)
      log.warn('Tracking error', error)
    }
  }

  // HTML 이스케이프
  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
